import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsCloudEndpointInitiatingContext } from 'src/hooks/useIsCloudEndpointInitiating';
import {
  CloudProvider,
  CreateRouter,
  CreateRouterVariables,
  RouterStatus,
  ShardStatus,
} from 'src/lib/graphqlTypes/types';

import { GraphRef } from '../../hooks/useGraphRef';

import { CREATE_CLOUD_ROUTER_MUTATION } from './mutations';
import { ErrorInput, RouterStatusMask } from './types';

export const durationInDays = (durationInSeconds?: number) =>
  durationInSeconds && durationInSeconds / 60 / 60 / 24;

export const shouldShowCloudRouterAlerts = (
  router: Omit<RouterStatusMask, 'constants'> | null | undefined,
): boolean => {
  if (!router) {
    return false;
  }

  // All DELETED routers should get lifecycle alerts
  if (router.status === RouterStatus.DELETED) {
    return true;
  }

  // All routers that are being CREATED or INITIALIZED get lifecycle alerts
  if (router.status === RouterStatus.CREATING) {
    return true;
  }

  // Only AWS routers should get non-DELETE lifecycle alerts
  if (router.shard?.region?.provider === 'AWS') {
    return Boolean(router.nextStatus) || router.status !== RouterStatus.RUNNING;
  } else {
    // Non-AWS routers only see AT-RISK/SHOULD MIGRATE alerts
    if (router.shard?.region?.provider === CloudProvider.FLY) {
      return true;
    }

    if (router.shard?.status === ShardStatus.DEPRECATED) {
      return true;
    }
  }

  // Handle routers without shard information (e.g., during creation)
  if (!router.shard || !router.shard.region || !router.shard.region.provider) {
    return false;
  }

  return Boolean(router.nextStatus) || router.status !== RouterStatus.RUNNING;
};

// Custom hook to track router status transitions
export const useRouterStatusTransition = (
  routerStatus: RouterStatus | undefined,
) => {
  // Custom state to track successful createRouter update
  const [routerIsRecreated, setRouterIsRecreated] = useState(false);
  const [routerRecreateIsErrored, setRouterRecreateIsErrored] = useState(false);
  const previousRouterStatus = useRef<RouterStatus | undefined>();

  useEffect(() => {
    if (
      previousRouterStatus.current === RouterStatus.CREATING &&
      routerStatus === RouterStatus.RUNNING
    ) {
      setRouterIsRecreated(true);
    }

    if (
      previousRouterStatus.current === RouterStatus.CREATING &&
      routerStatus === RouterStatus.ROLLING_BACK
    ) {
      setRouterRecreateIsErrored(true);
    }

    // Update the previous router status
    previousRouterStatus.current = routerStatus;
  }, [routerStatus]);

  return { routerIsRecreated, routerRecreateIsErrored };
};

export const useCreateRouter = (graphRef: GraphRef) => {
  const { refetch } = useIsCloudEndpointInitiatingContext();

  const handleComplete = useCallback(() => {
    // Due to the latency of provisioning a router when re-creating a router (i.e.
    // re-provisioning an AWS-hosted router provisioning can take up to
    // 5 minutes), we do not rely on the return value of the mutation
    // to determine whether a router is actually being provisioned.
    // We need to make a query for the router, complete checks to understand its
    // true state, and also poll for the latest information on the router status
    // until the router has been provisioned. To do so we leverage the existing
    // isCloudEndpointInitiating hook that can trigger this query via a refetch
    // call.
    refetch();
  }, [refetch]);

  const handleError = useCallback((error: ErrorInput) => {
    Sentry.captureException(error);
  }, []);

  const [createRouterMutation] = useMutation<
    CreateRouter,
    CreateRouterVariables
  >(CREATE_CLOUD_ROUTER_MUTATION, {
    onCompleted: handleComplete,
    onError: handleError,
  });

  const createRouter = useCallback(async () => {
    try {
      const response = await createRouterMutation({
        variables: {
          input: {
            orderingId: new Date().toISOString(), // Generate the UTC ISO 8601 time when request was generated
          },
          name: graphRef.graphVariant,
          graphId: graphRef.graphId,
        },
      });

      const createRouterResult = response.data?.graph?.variant?.createRouter;

      if (!createRouterResult) {
        // Capture GraphQL server errors
        return handleError(new Error('No result from createRouter mutation.'));
      }

      if (
        response.data &&
        response.data.graph &&
        response.data.graph.variant?.createRouter
      ) {
        // Capture errors unique to the GraphQL mutation
        switch (createRouterResult.__typename) {
          case 'InvalidInputErrors':
            return handleError({
              __typename: 'InvalidInputErrors',
              errors: createRouterResult.errors,
              message: createRouterResult.message,
            });
          case 'InternalServerError':
            return handleError({
              __typename: 'InternalServerError',
              message: createRouterResult.message,
            });
          case 'CreateRouterSuccess':
            return handleComplete();
          default:
            return handleError(
              new Error(
                `Unexpected error on createRouterMutation: ${createRouterResult}`,
              ),
            );
        }
      }
    } catch (err) {
      // capture any network errors
      if (err instanceof Error) {
        return handleError(err);
      } else {
        return handleError(
          new Error(
            `Caught an exception that is not an Error instance: ${err}`,
          ),
        );
      }
    }
  }, [graphRef, createRouterMutation, handleComplete, handleError]);

  return { createRouter };
};
